import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Badge,
 Box,
 Button,
 Flex,
 IconButton,
 Input,
 Table,
 TableContainer,
 Tbody,
 Td,
 Text,
 Th,
 Thead,
 Tooltip,
 Tr,
 useToast,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import baseService from '../../services/base';
import { fetchData } from '../../services/fetch';
import { Select } from '../../components/Input';
import Loader from '../../components/Feedback/Loader';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import { AttentionModal } from '../../components/Modals';
import GriIndicatorModal from './GriIndicatorModal';
import { useGriSeries } from '../../hooks/useGriSeries';

export default function GriIndicators() {
 const toast = useToast();

 const [search, setSearch] = useState('');
 const [series, setSeries] = useState('');
 const [editing, setEditing] = useState(null);
 const [formOpen, setFormOpen] = useState(false);
 const [toDelete, setToDelete] = useState(null);
 const [isDeleting, setIsDeleting] = useState(false);

 const { data: indicators, isLoading, error, mutate } = useSWR('gri-indicator', fetchData);
 const { options: seriesOptions, labels: seriesLabels, colors: seriesColors } = useGriSeries();

 // Catálogo é pequeno (~60 linhas) e estático: filtrar em memória evita uma
 // ida ao servidor a cada tecla.
 const filtered = useMemo(() => {
  const term = search.trim().toLowerCase();
  return (indicators || []).filter((indicator) => {
   if (series && indicator.series !== series) return false;
   if (!term) return true;
   return (
    indicator.code?.toLowerCase().includes(term) ||
    indicator.name?.toLowerCase().includes(term) ||
    seriesLabels[indicator.series]?.toLowerCase().includes(term)
   );
  });
 }, [indicators, search, series, seriesLabels]);

 function openCreate() {
  setEditing(null);
  setFormOpen(true);
 }

 function openEdit(indicator) {
  setEditing(indicator);
  setFormOpen(true);
 }

 async function confirmDelete() {
  setIsDeleting(true);
  try {
   await baseService.delete(`gri-indicator/${toDelete.id_gri_indicator}`);
   toast({ title: 'Indicador excluído', status: 'success' });
   await mutate();
  } catch {
   toast({ title: 'Não foi possível excluir o indicador', status: 'error' });
  } finally {
   setIsDeleting(false);
   setToDelete(null);
  }
 }

 return (
  <MainContent>
   <PageHeaderStructure
    title='Indicadores GRI'
    subtitle='Catálogo global de indicadores GRI Standards usado para classificar as questões dos templates.'
   >
    <Button variant='primary' width='230px' height='59px' onClick={openCreate}>
     Novo Indicador GRI
    </Button>
   </PageHeaderStructure>

   <Flex gap='12px' flexWrap='wrap' align='flex-end' mt='24px' mb='16px'>
    <Box w='320px'>
     <Text fontSize='xs' color='#818A8F' mb='8px'>
      Buscar
     </Text>
     <Input
      placeholder='Código, nome ou série'
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      bg='white'
     />
    </Box>

    <Box w='200px'>
     <Select
      name='series'
      label='Série'
      value={series}
      onChange={(e) => setSeries(e.target.value)}
     >
      <option value=''>Todas</option>
      {seriesOptions.map((option) => (
       <option key={option.value} value={option.value}>
        {option.label}
       </option>
      ))}
     </Select>
    </Box>

    <Button
     variant='ghost'
     isDisabled={!search && !series}
     onClick={() => {
      setSearch('');
      setSeries('');
     }}
    >
     Limpar filtros
    </Button>

    <Text fontSize='sm' color='#818A8F' ml='auto'>
     {filtered.length} de {indicators?.length || 0} indicadores
    </Text>
   </Flex>

   {error && <Text color='red.600'>Erro ao carregar os indicadores.</Text>}

   {isLoading ? (
    <Loader />
   ) : (
    <TableContainer bg='white' borderRadius='12px' p='8px'>
     <Table variant='simple'>
      <Thead>
       <Tr>
        <Th>Código GRI</Th>
        <Th>Nome</Th>
        <Th>Série</Th>
        <Th>Unidade de medida</Th>
        <Th isNumeric>Ações</Th>
       </Tr>
      </Thead>
      <Tbody>
       {filtered.map((indicator) => (
        <Tr key={indicator.id_gri_indicator}>
         <Td whiteSpace='nowrap' fontWeight={600}>
          {indicator.code}
         </Td>
         <Td whiteSpace='normal'>{indicator.name}</Td>
         <Td>
          <Badge
           colorScheme={seriesColors[indicator.series] || 'gray'}
           textTransform='none'
          >
           {seriesLabels[indicator.series] || indicator.series}
          </Badge>
         </Td>
         <Td whiteSpace='nowrap'>{indicator.unit}</Td>
         <Td isNumeric>
          <Flex gap='8px' justify='flex-end'>
           <Tooltip label='Editar' hasArrow>
            <IconButton
             aria-label='Editar indicador'
             icon={<FiEdit2 />}
             size='sm'
             variant='ghost'
             onClick={() => openEdit(indicator)}
            />
           </Tooltip>
           <Tooltip label='Excluir' hasArrow>
            <IconButton
             aria-label='Excluir indicador'
             icon={<FiTrash2 />}
             size='sm'
             variant='ghost'
             colorScheme='red'
             onClick={() => setToDelete(indicator)}
            />
           </Tooltip>
          </Flex>
         </Td>
        </Tr>
       ))}
      </Tbody>
     </Table>

     {filtered.length === 0 && (
      <Text px='16px' py='24px' color='#818A8F'>
       {indicators?.length
        ? 'Nenhum indicador para estes filtros.'
        : 'Nenhum indicador cadastrado.'}
      </Text>
     )}
    </TableContainer>
   )}

   <GriIndicatorModal
    isOpen={formOpen}
    onClose={() => setFormOpen(false)}
    indicator={editing}
    onSaved={mutate}
   />

   {/* GRI-06: estava invertido. `onClose` é o botão "Não" e `submit` é o
       "Sim"; não existe prop `cancel`. Do jeito anterior, recusar excluía o
       indicador e confirmar não fazia nada. */}
   <AttentionModal
    isOpen={!!toDelete}
    onClose={() => setToDelete(null)}
    submit={confirmDelete}
    isLoading={isDeleting}
    title='Excluir indicador GRI'
    content={`O indicador "${toDelete?.code || ''}" sai do catálogo, mas os vínculos já existentes são preservados.`}
   />
  </MainContent>
 );
}
